import { Trans } from 'react-i18next';
import { notification, message } from 'antd';
import moment from 'moment';
import qrcodeGenerator from 'qrcode-generator';
import axios from 'axios';
import i18next from 'i18next';

import { FileAPIMessages } from '@Constants/fileConstants';

import { timeFormatYMDHMZ } from './commonVariable';
import { OTHER_CHOICE_ID } from './commonParameters';

import * as API from '../api';

// funevent domain
export function getBaseURL(infix = '') {
  const baseURL = `https://funevent${infix}.azurewebsites.net`;

  return baseURL;
}

export function getEnvironmentBaseURL() {
  const devUrl = getBaseURL('-backend-dev');
  const stageUrl = getBaseURL('-backend-stage');
  const liveUrl = getBaseURL('-backend');
  const LOCAL_SERVER_HOST = devUrl;
  const { origin } = window.location;

  if (process.env.NODE_ENV === 'development') return LOCAL_SERVER_HOST;
  if (origin) {
    if (origin.includes('entry-dev')) {
      return devUrl;
    }
    if (origin.includes('entry-stage')) {
      return stageUrl;
    }

    return liveUrl;
  }

  return stageUrl;
}

// onsite domain
export function getOnSiteBaseURL(infix = '') {
  const baseURL = `https://onsite${infix}.azurewebsites.net`;

  return baseURL;
}

export function getEnvironmentOnSiteBaseURL() {
  const devUrl = getOnSiteBaseURL('-backend-dev');
  const stageUrl = getOnSiteBaseURL('-backend-stage');
  const liveUrl = getOnSiteBaseURL('-backend');
  const LOCAL_SERVER_HOST = devUrl;
  const { origin } = window.location;

  if (process.env.NODE_ENV === 'development') return LOCAL_SERVER_HOST;
  if (origin) {
    if (origin.includes('entry-dev')) {
      return devUrl;
    }
    if (origin.includes('entry-stage')) {
      return stageUrl;
    }

    return liveUrl;
  }

  return stageUrl;
}

// 頁面 URL
export function getAppUrl() {
  const { origin } = window.location;
  return process.env.NODE_ENV === 'development' ? `${origin}/#` : `${origin}/app/#`;
}

// 各站點外部連結（依環境區分）
export function getEnvironmentSiteLinks() {
  const { origin } = window.location;
  const isDev = process.env.NODE_ENV === 'development' || origin.includes('entry-dev');
  const isStage = !isDev && origin.includes('entry-stage');

  if (isDev) {
    return [
      { key: 'onsite', url: 'https://funevent-entry-dev.azurewebsites.net/onsite/#/' },
      { key: 'luckydraw', url: 'https://funevent-entry-dev.azurewebsites.net/luckydraw/#/' },
      { key: 'stock', url: 'https://funezstock-dev.azurewebsites.net/#/' },
      {
        key: 'frontend',
        url: 'https://gray-mushroom-0950d1000-dev.eastasia.6.azurestaticapps.net/#/',
      },
    ];
  }

  if (isStage) {
    return [
      { key: 'onsite', url: 'https://funevent-entry-stage.azurewebsites.net/onsite/#/' },
      { key: 'qrcode', url: 'https://qr-code-check-staging.azurewebsites.net/frontend/#/' },
      { key: 'luckydraw', url: 'https://funevent-entry-stage.azurewebsites.net/luckydraw/#/' },
      { key: 'stock', url: 'https://funezstock-stage.azurewebsites.net/#/' },
      {
        key: 'frontend',
        url: 'https://gray-mushroom-0950d1000-stage.eastasia.6.azurestaticapps.net/#/',
      },
    ];
  }

  // 正式站
  return [
    { key: 'onsite', url: 'https://funevent.net/onsite/#/' },
    { key: 'qrcode', url: 'https://qr-code-check.azurewebsites.net/frontend/#/' },
    { key: 'luckydraw', url: 'https://funevent.net/luckydraw/#/' },
    { key: 'stock', url: 'https://funezstock.net/#/' },
    { key: 'frontend', url: 'https://gray-mushroom-0950d1000.6.azurestaticapps.net/#/' },
  ];
}

// 活動報到平台 URL
export function getOnSiteAppUrl() {
  const { origin } = window.location;
  return process.env.NODE_ENV === 'development'
    ? 'http://localhost:3002/onsite'
    : `${origin}/onsite`;
}

export const getUserLanguage = () => {
  let systemLanguage = 'en-US';
  if (
    JSON.parse(window.localStorage.getItem('persist:env')) === undefined ||
    JSON.parse(window.localStorage.getItem('persist:env')) === null
  ) {
    return systemLanguage;
  }
  const { userLanguage } = JSON.parse(window.localStorage.getItem('persist:env'));
  switch (userLanguage) {
    case '"en-US"':
      systemLanguage = 'en-US';
      break;
    case '"zh-TW"':
      systemLanguage = 'zh-TW';
      break;
    case '"default"':
      systemLanguage = window.navigator.language;
      break;
    default:
      systemLanguage = 'en-US';
  }

  return systemLanguage;
};

// 檢查密碼是否包含英文與數字，有的話回傳 true；反之
export function checkPassword(password) {
  let checkPasswordEnglish = false;
  let checkPasswordNumber = false;
  for (let i = 0; i < password.length; i += 1) {
    if (
      (password.charCodeAt(i) >= 65 && password.charCodeAt(i) <= 90) ||
      (password.charCodeAt(i) >= 97 && password.charCodeAt(i) <= 122)
    ) {
      checkPasswordEnglish = true;
    } else if (password.charCodeAt(i) >= 48 && password.charCodeAt(i) <= 57) {
      checkPasswordNumber = true;
    }
  }

  if (checkPasswordEnglish && checkPasswordNumber) {
    return true;
  }

  return false;
}

// TODO:待修改成 RegExp
// 檢查 email 值含 "@" 符號
// "@" 後面要有字串
// 不能以 "@" 開頭
// 不能有兩個以上的 "@"
// 至少要有一個 "."
// "." 前後都要有字元
// "." 的前一個字元不能是 "@"
export function checkValidEmailFormat(emailValue) {
  if (!emailValue) return false;

  const AT_CHAR_CODE = 64;
  const POINT_CHAR_CODE = 46;
  // 字元數量
  let atCharacterCount = null;
  let pointCharacterCount = null;
  // 字元位置
  let atCharacterPoistion = null;

  const emailValueToString = emailValue.toString();

  if (!emailValueToString) return;

  if (emailValueToString.charCodeAt(0) === AT_CHAR_CODE) {
    // 不能以 @ 開頭
    return false;
  }

  for (let i = 0; i < emailValueToString.length; i += 1) {
    const charCode = emailValueToString.charCodeAt(i);

    // 遇到 @ 就 + 1 數量, 數量不能超過 1
    if (charCode === AT_CHAR_CODE) {
      atCharacterPoistion = i;
      atCharacterCount += 1;
    }
    if (atCharacterCount > 1) {
      return false;
    }

    // @ 後面至少三個字元
    if (charCode === AT_CHAR_CODE && emailValueToString[i + 3] === undefined) {
      return false;
    }

    if (charCode === POINT_CHAR_CODE) {
      pointCharacterCount += 1;
    }

    // 以下為 . 在 @ 後面的規則
    // . 前後至少都要有一個字元
    // . 前面不能是 @
    if (
      i > atCharacterPoistion &&
      charCode === POINT_CHAR_CODE &&
      (emailValueToString[i - 1] === undefined ||
        emailValueToString[i + 1] === undefined ||
        emailValueToString[i - 1] === '@')
    ) {
      return false;
    }
  }

  // 沒有 @ 或 .
  if (atCharacterCount === null || pointCharacterCount === null) {
    return false;
  }

  return true;
}

// 台灣手機電話 檢查
export function checkValidCellPhoneFormat(phoneValue) {
  if (!phoneValue) return false;
  const phoneValueToString = phoneValue?.toString();
  if (!phoneValueToString) return false;

  const regexPattern = /^09[0-9]{8}$/;
  const internationalRegexPattern = /^[+]8869[0-9]{8}$/;

  const isPatternTestPass = regexPattern?.test(phoneValue);
  const isInternationalPatternTestPass = internationalRegexPattern?.test(phoneValue);

  if (isPatternTestPass || isInternationalPatternTestPass) {
    return true;
  }

  return false;
}

// 檢查物件的 key 的長度（主要用於檢查 store 的 state 收到 API 的 response 了沒，
// 若沒有，就會是 state 設定的初始值，目前都設定初始值為 {}，因此沒收到的話，以下結果的陣列長度都會是 0；反之）
export function checkObjectLength(globalState) {
  const objectLength = Object.keys(globalState).length;
  return objectLength;
}

// unlayer initialize
export const initializeUnlayerEditor = (unlayer, id, tagData = '') => {
  const editor = unlayer.createEditor({
    id,
    version: '1.157.0',
    displayMode: 'email',
    locale: getUserLanguage(),
    tools: {
      form: { enabled: false },
      timer: { enabled: false },
      video: { enabled: false },
    },
    features: { stockImages: false },
    safeHtml: true,
    mergeTags: {
      ...tagData,
    },
  });

  return editor;
};

export const mergeTagsKeyAndValue = (name, value) => ({
  name,
  value: `{{${value}}}`,
});

// TODO: 換變數名稱，與上面做區隔，前面只有一個大括號
export const emailTitleMergeTagsKeyAndValue = (name, value) => ({
  name,
  value: `${value}}}`,
});

// 錯誤提示框，預設是 401 的錯誤提示
export function errorNotification(
  message = <Trans i18nKey="common.errorMessage">Error Message</Trans>,
  description = (
    <Trans i18nKey="common.unauthorizedMessage">Oops！ The account is unauthorized.</Trans>
  ),
) {
  return notification.error({
    key: 'unauthorized',
    message,
    description,
    duration: 8,
  });
}

// 參加者名單列表 API response 的資料與 Table 的格式不符，因此用此函式轉換成 Table 可以用的格式
export function getTransformedColumns(columns) {
  // response 回來的 orderBy 會亂掉，所以先排序
  columns.sort((a, b) => a.orderBy - b.orderBy);

  let transformedColumns = [];
  columns.forEach((column) => {
    const columnObject = { dataIndex: column.orderBy.toString(), title: column.name, width: 150 };
    transformedColumns = [...transformedColumns, columnObject];
  });

  return transformedColumns;
}

// 匯出檔案,blob 形式
export const exportBlobFile = (data, fileName, fileNameExtension = 'xlsx') => {
  const blob = new Blob([data]);
  const url = window.URL.createObjectURL(blob);
  const linkElement = document.createElement('a');
  linkElement.href = url;
  linkElement.setAttribute('download', `${fileName}.${fileNameExtension}`);
  document.body.appendChild(linkElement);
  linkElement.click();
  document.body.removeChild(linkElement);
};

// 取得當前分頁的內容
export const getPresentPageContent = (index, presentPage, pageSize) => {
  if (presentPage === null || pageSize === null) return undefined;
  const previousPage = (presentPage - 1) * pageSize;
  const thisPage = presentPage * pageSize;
  return index + 1 <= thisPage && index + 1 > previousPage;
};

// 取得當前時間並轉換顯示的格式
export const getNowTimeAndTransferFormat = () => {
  const nowTime = new Date();
  const transferNowTime = moment(nowTime).format(timeFormatYMDHMZ);
  return transferNowTime;
};

// 字串是否 ellipsis
export const getIsStringEllipsis = (e) => {
  return e.offsetWidth < e.scrollWidth;
};

// 排序問卷有包含"其它"選項的題型，如單選題和多選題，0 預設為"其它"選項
export const sortSurveyFormChoicesQuestionIncludedOther = (a, b) => {
  if (a !== OTHER_CHOICE_ID && b !== OTHER_CHOICE_ID) return Number(a) - Number(b);

  if (a === OTHER_CHOICE_ID) return 1;

  if (b === OTHER_CHOICE_ID) return -1;
};

// 取得排序結果
export const getSortedResult = (a, b, locales = 'en') => {
  const index = a.toString().localeCompare(b.toString(), locales);
  return index;
};

export const transformServerWebImageToBase64 = (imageUrl, onload) => {
  return new Promise((resolve, reject) => {
    if (!imageUrl) {
      resolve('');
      return;
    }

    axios
      .get(imageUrl, { responseType: 'blob' })
      .then((res) => {
        if (!res?.data) return;

        if (!res.data?.type || !res.data.type.includes('image')) {
          message.error(
            <Trans i18nKey="common.parseWebImageFailed">
              解析網路圖片失敗，請使用正確的圖片類型
            </Trans>,
          );
          return;
        }

        const reader = new FileReader();

        reader.readAsDataURL(res.data);

        reader.onloadend = () => {
          onload && onload();

          const base64URI = reader.result;

          if (base64URI) resolve(base64URI);
        };

        reader.onerror = () => {
          reject('ERROR');
        };
      })
      .catch((error) => {
        reject(error);
      });
  });
};

// get uploaded image's Base64 URL
export const getBase64URLOfUploadedImage = (e, isShownFileName = false, onload) => {
  return new Promise((resolve, reject) => {
    let imageUri = '';
    let fileName = '';

    if (e?.target?.files.length === 0) return;
    const fileObject = e.target.files[0];
    fileName = e.target.files[0].name;

    if (fileObject?.type === '' || !fileObject.type.includes('image')) {
      message.error(
        <Trans i18nKey="common.uploadImageFailed">上傳失敗，請上傳圖片類型的檔案</Trans>,
      );
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(fileObject);

    reader.onload = () => {
      onload && onload();

      if (reader.result) {
        imageUri = reader.result;

        if (isShownFileName) {
          resolve({ imageUri, fileName });
        } else {
          resolve(imageUri);
        }

        return;
      }

      resolve('NO RESULT');
    };

    reader.onerror = () => {
      reject('ERROR');
    };
  });
};

export const getUploadedImageLink = async (e) => {
  const result = {
    imageLink: '',
    message: '',
    returnCode: 0,
    filename: '',
  };

  if (!e?.target && !e?.attachments) return result;

  const formData = new FormData();
  const fileObject = e.attachments ? e.attachments[0] : e?.target?.files[0];
  result.filename = fileObject?.name || '';
  formData.append('file', fileObject);

  if (!formData) return result;

  if (fileObject?.type === '' || !fileObject?.type.includes('image')) {
    result.message = i18next.t('common.uploadImageFailed');

    message.error(<Trans i18nKey="common.uploadImageFailed">上傳失敗，請上傳圖片類型的檔案</Trans>);

    return { ...result, returnCode: 1 };
  }

  await API.postFileUpload(formData)
    .then((res) => {
      if (!res?.data) {
        result.message = 'invalid format';
        result.returnCode = 1;
        return result;
      }

      const { data, msg, returnCode } = res.data;

      if ((returnCode !== 0 && !returnCode) || !msg) {
        result.message = 'invalid format';
        result.returnCode = 1;
        return result;
      }

      result.message = msg;
      result.returnCode = returnCode;

      if (returnCode === 1) {
        if (msg === FileAPIMessages.FILE_SIZE_GREATER_THAN_100000_BYTES) {
          result.message = i18next.t('common.uploadImageOutOfMaxSize');

          message.error(
            <Trans i18nKey="common.uploadImageOutOfMaxSize">上傳失敗，檔案大小超過 1MB</Trans>,
          );
          return result;
        }

        message.error(msg);
        return result;
      }

      if (!data) {
        result.message = 'invalid format';
        result.returnCode = 1;
        return result;
      }

      result.imageLink = data;
    })
    .catch((error) => {
      result.message = error.message;
      result.returnCode = 1;
      message.error(`${error.message} 上傳圖片失敗` || 'ERROR STATUS');
    });

  return result;
};

/**
 *
 * @param {string[]} array
 * @returns
 */
export const getLongestValueIndex = (array) => {
  let theLongestIndex = null;
  const lengthArray = array.map((element) => element.length);

  theLongestIndex = lengthArray.indexOf(Math.max(...lengthArray));

  return theLongestIndex;
};

/**
 *
 * @param {string} content : ;
 * @param {number} typeNumber
 * @param {string} errorCorrectionLevel
 * @param {string} mode
 * @returns {string} default source: base64 data url, otherwise, empty string
 */
export const generateQRCodeResource = (
  content,
  typeNumber = 0,
  errorCorrectionLevel = 'M',
  mode = 'Byte',
) =>
  /**
   *
   * @param {number} cellSize
   * @param {string} createType data url, svg, image
   */
  (cellSize, createType = 'dataURL') => {
    const qr = qrcodeGenerator(typeNumber, errorCorrectionLevel);

    if (!content) return '';
    qr.addData(content, mode);
    qr.make();

    let qrResource = '';

    if (createType === 'dataURL') {
      qrResource = qr.createDataURL(cellSize);
    }

    if (createType === 'svg') {
      qrResource = qr.createSvgTag({ cellSize, scalable: true });
    }

    if (createType === 'image') {
      qrResource = qr.createImgTag(cellSize);
    }

    return qrResource;
  };

/**
 * parse blob data
 * if data is returnCode 1 or other API failed, JSON.parse reader.result
 */
export const parseBlobData = async (blobData, onLoad, onError) => {
  const JSON_TYPE = 'application/json';
  const reader = new FileReader();
  let result = '';

  const isBlobType = blobData instanceof Blob;

  if (!isBlobType) {
    return '';
  }

  reader.readAsText(blobData);

  reader.onload = () => {
    if (blobData.type === JSON_TYPE) {
      result = JSON.parse(reader.result);
      onLoad && onLoad(result);
      return;
    }

    onLoad && onLoad(result);
  };

  reader.onerror = () => {
    onError && onError();
  };

  return result;
};
